import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Skeleton, Space, Typography } from "antd";
import {
  ArrowLeftOutlined,
  BankOutlined,
  EnvironmentOutlined,
  FileImageOutlined,
  GlobalOutlined,
  MailOutlined,
  PhoneOutlined,
  SafetyCertificateOutlined,
  ShopOutlined,
  StarOutlined,
  TagsOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import SidePromotionSection from "components/promotions/SidePromotionSection";
import apiClient from "utils/apiClient";
import { STORAGE_BASE_URL } from "constants";
import { OLIVEEB_COLORS } from "workspaces/colors";
import "./PublicOrganizationDetailsPage.css";

const { Paragraph, Text, Title } = Typography;

const firstText = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number") return String(value);
  }
  return "";
};

const titleCase = (value) =>
  String(value || "N/A")
    .trim()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const detailRows = (rows) =>
  rows
    .map((row) => ({ ...row, value: firstText(row.value, "N/A") }))
    .filter((row) => row.value && row.value !== "N/A");

const directFileUrlValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    const trimmed = value.trim();
    return /^(https?:|data:|blob:)/i.test(trimmed) ? trimmed : "";
  }
  if (typeof value !== "object") return "";
  return (
    value.signedUrl ||
    value.signedURL ||
    value.signed_url ||
    value.downloadUrl ||
    value.downloadURL ||
    value.download_url ||
    value.previewUrl ||
    value.previewURL ||
    value.preview_url ||
    value.publicUrl ||
    value.publicURL ||
    value.public_url ||
    value.fileUrl ||
    value.fileURL ||
    value.file_url ||
    value.mediaUrl ||
    value.mediaURL ||
    value.media_url ||
    value.url ||
    value.uri ||
    value.href ||
    directFileUrlValue(value.file) ||
    directFileUrlValue(value.data) ||
    ""
  );
};

const fileIdValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed || /^(https?:|data:|blob:)/i.test(trimmed)) return "";
    return trimmed.replace(/^file-service:\/\//i, "");
  }
  if (typeof value !== "object") return "";
  return (
    value.fileId ||
    value.fileID ||
    value.file_id ||
    value.id ||
    value.uuid ||
    value.file?.fileId ||
    value.file?.id ||
    value.file?.uuid ||
    ""
  );
};

const fileSource = (...values) => {
  let fallbackFileId = "";
  for (const value of values) {
    const directUrl = directFileUrlValue(value);
    if (directUrl) return { url: directUrl, fileId: "" };
    const fileId = fileIdValue(value);
    if (!fallbackFileId && fileId) fallbackFileId = fileId;
  }
  return fallbackFileId
    ? {
        url: `${STORAGE_BASE_URL}/api/v1/files/${encodeURIComponent(fallbackFileId)}`,
        fileId: fallbackFileId,
      }
    : { url: "", fileId: "" };
};

const responseUrlValue = (payload, visited = new WeakSet()) => {
  if (!payload) return "";
  if (typeof payload === "string") return payload.trim();
  if (typeof payload !== "object") return "";
  if (visited.has(payload)) return "";
  visited.add(payload);
  if (Array.isArray(payload)) {
    for (const item of payload) {
      const url = responseUrlValue(item, visited);
      if (url) return url;
    }
    return "";
  }
  const directUrl = directFileUrlValue(payload);
  if (directUrl) return directUrl;
  for (const value of Object.values(payload)) {
    const url = responseUrlValue(value, visited);
    if (url) return url;
  }
  return "";
};

const resolveSignedFileUrl = async (fileId) => {
  if (!fileId) return "";
  try {
    const response = await apiClient.get("/storage-service/api/v1/files/photo/signed-url", {
      params: { fid: fileId },
    });
    const url = responseUrlValue(response?.data);
    if (url) return url;
  } catch {
    // Fall back to batch endpoint.
  }
  try {
    const response = await apiClient.get("/storage-service/api/v1/files/photo/signed-urls", {
      params: { fids: fileId },
    });
    return responseUrlValue(response?.data);
  } catch {
    return "";
  }
};

const organizationName = (organization) =>
  firstText(organization?.name, organization?.organizationName, organization?.businessName, "Organization");

const serviceLabel = (service) =>
  firstText(service?.name, service?.label, service?.title, service?.code, service);

const organizationOfferings = (organization) => {
  const offerings =
    organization?.businessOfferings ||
    organization?.organizationOfferings ||
    organization?.organizationServices ||
    organization?.services ||
    [];
  return Array.isArray(offerings) ? offerings.map(serviceLabel).filter(Boolean) : [];
};

const contactInfo = (organization) => organization?.contactInformation || organization?.contact || {};
const locationInfo = (organization) => organization?.locationInformation || organization?.location || {};

const locationText = (organization) => {
  const location = locationInfo(organization);
  return [
    location.addressLine1,
    location.addressLine2,
    location.city,
    location.county,
    location.state,
    location.zipCode,
    location.country,
  ].filter(Boolean).join(", ");
};

const mediaSources = (organization) => [
  {
    key: "logo",
    label: "Logo",
    source: fileSource(
      organization?.logo,
      organization?.logoUrl,
      organization?.logoUri,
      organization?.logoSignedUrl,
      organization?.logoDownloadUrl,
    ),
  },
  {
    key: "profilePhoto",
    label: "Profile photo",
    source: fileSource(
      organization?.profilePhoto,
      organization?.profilePhotoUrl,
      organization?.profilePhotoUri,
      organization?.profilePhotoSignedUrl,
      organization?.profilePhotoDownloadUrl,
    ),
  },
].filter((item) => item.source.url || item.source.fileId);

function DetailCard({ title, icon, rows, children }) {
  if ((!rows || rows.length === 0) && !children) return null;
  return (
    <section className="public-org-card">
      <div className="public-org-card__head">
        <span>{icon}</span>
        <strong>{title}</strong>
      </div>
      {rows?.length ? (
        <div className="public-org-detail-list">
          {rows.map((row) => (
            <div className="public-org-detail-row" key={row.label}>
              <span>{row.label}</span>
              <strong>{row.value}</strong>
            </div>
          ))}
        </div>
      ) : null}
      {children}
    </section>
  );
}

export default function PublicOrganizationDetailsPage() {
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const [organization, setOrganization] = useState(null);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadOrganization = async () => {
      if (!organizationId) {
        setError("Organization ID is missing.");
        setLoading(false);
        return;
      }

      const endpoints = [
        `/profile-service/api/v1/organizations/${organizationId}`,
        `/profile-service/api/v1/organizations/${organizationId}/mini`,
      ];

      try {
        setLoading(true);
        setError("");
        let loaded = null;
        for (const endpoint of endpoints) {
          try {
            const response = await apiClient.get(endpoint);
            loaded = response?.data?.data || response?.data;
            if (loaded) break;
          } catch {
            // Try the next compatible organization endpoint.
          }
        }
        if (!loaded) throw new Error("Unable to load organization details.");
        if (!cancelled) setOrganization(loaded);
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.message || err?.message || "Unable to load organization details.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadOrganization();

    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  useEffect(() => {
    let cancelled = false;

    const loadMedia = async () => {
      const sources = mediaSources(organization);
      const resolved = await Promise.all(
        sources.map(async (item) => ({
          ...item,
          url: item.source.fileId
            ? await resolveSignedFileUrl(item.source.fileId).then((url) => url || item.source.url)
            : item.source.url,
        })),
      );
      if (!cancelled) setMedia(resolved.filter((item) => item.url));
    };

    if (organization) loadMedia();
    else setMedia([]);

    return () => {
      cancelled = true;
    };
  }, [organization]);

  const offerings = useMemo(() => organizationOfferings(organization), [organization]);
  const contact = contactInfo(organization);
  const location = locationInfo(organization);
  const address = locationText(organization);
  const mapHref = address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
    : "";
  const goBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/organizations");
  };

  const detailRowsForOrganization = detailRows([
    { label: "Name", value: organizationName(organization) },
    { label: "Description", value: organization?.description },
    {
      label: "Type",
      value: firstText(
        organization?.organizationLocationType?.name,
        organization?.organizationLocationType?.label,
        organization?.businessLocationType?.name,
        organization?.type,
      ),
    },
    { label: "Status", value: titleCase(organization?.status || "PENDING_REVIEW") },
  ]);

  const registrationRows = detailRows([
    { label: "EIN", value: organization?.ein },
    { label: "Certificate", value: fileIdValue(organization?.certificate) || directFileUrlValue(organization?.certificate) ? "File attached" : "" },
  ]);

  const contactRows = detailRows([
    { label: "Primary email", value: firstText(contact.primaryEmail, organization?.email) },
    { label: "Secondary email", value: contact.secondaryEmail },
    { label: "Primary phone", value: firstText(contact.primaryPhone, organization?.phone) },
    { label: "Secondary phone", value: contact.secondaryPhone },
    { label: "Website", value: firstText(contact.websiteUrl, organization?.websiteUrl) },
    { label: "Contact person", value: contact.contactPersonName },
    { label: "Contact title", value: contact.contactPersonTitle },
  ]);

  const locationRows = detailRows([
    { label: "Address line 1", value: location.addressLine1 },
    { label: "Address line 2", value: location.addressLine2 },
    { label: "City", value: location.city },
    { label: "County", value: location.county },
    { label: "State", value: location.state },
    { label: "Country", value: location.country },
    { label: "Zip code", value: location.zipCode },
  ]);

  return (
    <main className="public-org-page">
      <div className="public-org-shell">
        <section className="public-org-main">
          {error ? (
            <Alert
              showIcon
              type="error"
              message="Unable to load organization"
              description={error}
              className="public-org-alert"
            />
          ) : null}

          {loading && !organization ? (
            <div className="public-org-loading">
              <Skeleton active paragraph={{ rows: 10 }} />
            </div>
          ) : null}

          {organization ? (
            <Space direction="vertical" size={14} className="public-org-stack">
              <section className="public-org-hero">
                <div className="public-org-hero__top">
                  <button
                    type="button"
                    className="public-org-back"
                    onClick={goBack}
                    aria-label="Go back"
                  >
                    <ArrowLeftOutlined />
                  </button>
                  <span className="public-org-hero__icon">
                    <BankOutlined />
                  </span>
                  <div>
                    <Title level={1}>{organizationName(organization)}</Title>
                    <Text>{address || "Location not set"}</Text>
                    <div className="public-org-hero__badges">
                      <span>
                        <ShopOutlined /> {titleCase(organization?.type)}
                      </span>
                      <span>
                        <SafetyCertificateOutlined /> {titleCase(organization?.status || "PENDING_REVIEW")}
                      </span>
                    </div>
                  </div>
                </div>
                {organization?.description ? (
                  <Paragraph>{organization.description}</Paragraph>
                ) : null}
                <div className="public-org-stats">
                  <span>
                    <strong>{offerings.length}</strong>
                    <small>Offerings</small>
                  </span>
                  <span>
                    <strong>{media.length}</strong>
                    <small>Media</small>
                  </span>
                </div>
              </section>

              <DetailCard title="Organization details" icon={<BankOutlined />} rows={detailRowsForOrganization} />

              <DetailCard title="Organization offerings" icon={<TagsOutlined />}>
                {offerings.length ? (
                  <div className="public-org-pill-list">
                    {offerings.map((offering) => (
                      <span key={offering}>{offering}</span>
                    ))}
                  </div>
                ) : (
                  <Text className="public-org-muted">No organization offerings selected.</Text>
                )}
              </DetailCard>

              <DetailCard title="Ratings & Reviews" icon={<StarOutlined />}>
                <Button className="public-org-review-link" htmlType="button">
                  View Reviews
                </Button>
              </DetailCard>

              <DetailCard title="Registration and media" icon={<FileImageOutlined />} rows={registrationRows}>
                {media.length ? (
                  <div className="public-org-media-row">
                    {media.map((item) => (
                      <figure key={item.key}>
                        <img
                          src={item.url}
                          alt=""
                          onError={(event) => {
                            if (item.source.fileId) {
                              const fallback = `${STORAGE_BASE_URL}/api/v1/files/${encodeURIComponent(item.source.fileId)}`;
                              if (event.currentTarget.src !== fallback) event.currentTarget.src = fallback;
                            }
                          }}
                        />
                        <figcaption>{item.label}</figcaption>
                      </figure>
                    ))}
                  </div>
                ) : null}
              </DetailCard>

              <DetailCard title="Contact information" icon={<MailOutlined />} rows={contactRows}>
                {contactRows.some((row) => row.label === "Website") ? (
                  <a className="public-org-website-link" href={firstText(contact.websiteUrl, organization?.websiteUrl)} target="_blank" rel="noreferrer">
                    <GlobalOutlined /> Visit website
                  </a>
                ) : null}
              </DetailCard>

              <DetailCard title="Location information" icon={<EnvironmentOutlined />} rows={locationRows}>
                {mapHref ? (
                  <a className="public-org-map-link" href={mapHref} target="_blank" rel="noreferrer">
                    <EnvironmentOutlined />
                    <span>
                      <strong>Open Map</strong>
                      <small>{address}</small>
                    </span>
                  </a>
                ) : null}
              </DetailCard>

              <DetailCard title="Organization users" icon={<TeamOutlined />}>
                <Text className="public-org-muted">Organization users are not shown on public details.</Text>
              </DetailCard>
            </Space>
          ) : null}
        </section>

        <aside className="public-org-rail">
          <SidePromotionSection visibleCount={3} />
        </aside>
      </div>
    </main>
  );
}
